<script>
  import { createEventDispatcher, onMount, tick } from "svelte";
  import {Button} from "paxray-components-svelte";
  import { ROBOT_2_ICON, UPLOAD_ICON } from '@utils/icons.js';

  export let allowFileUpload = false;
  export let dictate = false;
  export let graphicEq = false;
  // svelte-ignore unused-export-let
  export let noticeTitle = "Pax AI";
  // svelte-ignore unused-export-let
  export let noticeMessage = "Choose how you would like to get started.";
  export let createLabel = "Start with Pax AI";
  export let importLabel = "Import";
  export let buttonSize = "field";
  export let showImport = true;
  export let startGreeting = "How would you like to get started?";
  export let startSubtitle = "Choose an option below to continue.";
  export let greeting = "Ready when you are.";
  export let placeholder = "Message Pax AI";
  export let submitLabel = "";
  export let submitDisabled = false;
  export let accept = "";
  export let multiple = false;

  export let message = "";
  export let moveToBottomOnSubmit = false;
  export let submitted = false;
  export let darkStartScreen = false;
  // svelte-ignore unused-export-let
  export let showNotice = true;
  // svelte-ignore unused-export-let
  export let workInProgressNotice = false;
  export let showSuggestions = true;
  export let showComposer = true;
  export let centerContent = false;
  export let showBackButton = false;
  export let started = false;
  export let processing = false;
  export let showGreetingWhenInput = false;
  export let processingTitle = "Processing your input";
  export let processingMessage = "This can take a moment. Please wait.";
  let fileInput;
  let messageInput;
  let isMultiline = false;
  let selectedFiles = [];
  let chatRoot;
  let viewBounds = { left: 0, width: 0 };
  const dispatch = createEventDispatcher();

  $: if (messageInput && message !== undefined) {
    tick().then(resizeMessageInput);
  }

  onMount(() => {
    const updateViewBounds = () => {
      const bounds = chatRoot?.getBoundingClientRect();
      if (bounds) viewBounds = { left: bounds.left, width: bounds.width };
    };

    updateViewBounds();
    const observer = new ResizeObserver(updateViewBounds);
    if (chatRoot) observer.observe(chatRoot);
    window.addEventListener("resize", updateViewBounds);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateViewBounds);
    };
  });

  function openFilePicker() {
    if (allowFileUpload) fileInput?.click();
  }

  function handleFilesSelected(event) {
    const files = Array.from(event.currentTarget.files || []);
    if (!files.length) return;

    selectedFiles = multiple ? [...selectedFiles, ...files] : files.slice(0, 1);
    selectedFiles = multiple ? [...selectedFiles, ...files] : files.slice(0, 1);
    dispatch("filesSelected", { files: selectedFiles });
  }

  function removeSelectedFile(index) {
    selectedFiles = selectedFiles.filter((_, fileIndex) => fileIndex !== index);
    if (fileInput) fileInput.value = "";
    dispatch("filesSelected", { files: selectedFiles });
  }

  function getFileType(file) {
    const extension = file.name.split(".").pop();
    return extension && extension !== file.name
      ? extension.toUpperCase()
      : "FILE";
  }

  function startCreation() {
    started = true;
  }

  function startImport() {
    dispatch("importFramework");
  }

  function handleSubmit() {
    if (submitDisabled || (!message.trim() && !selectedFiles.length)) return;
    if (moveToBottomOnSubmit) submitted = true;
    dispatch("submit", { message, files: selectedFiles });
    if (selectedFiles.length) {
      selectedFiles = [];
      if (fileInput) fileInput.value = "";
    }
  }

  function resizeMessageInput() {
    const input = messageInput;
    if (!input) return;
    input.style.height = "auto";
    input.style.height = `${input.scrollHeight}px`;
    isMultiline = input.scrollHeight > 2.35 * 16 + 2;
  }
</script>

<div
  bind:this={chatRoot}
  style={`--paxbot-view-left: ${viewBounds.left}px; --paxbot-view-width: ${viewBounds.width}px;`}
  class="paxbot-fullscreen-chat"
  class:hasInput={submitted}
  class:show-greeting-when-input={showGreetingWhenInput}
  class:center-content={centerContent}
  class:started
  class:has-progress={started && Boolean($$slots.progress)}
  class:dark-start={darkStartScreen}
  aria-label="Pax AI Chat"
>
  {#if started && $$slots.progress}
    <div class="chat-progress">
      <slot name="progress" />
    </div>
  {/if}
  <main class="chat-stage">
    {#if !started}
      <div class="start-screen">
        <h1>{startGreeting}</h1>
        <p class="start-subtitle">{startSubtitle}</p>
        <div class="start-actions">
          <Button
            kind={darkStartScreen ? "paxAI" : "secondary"}
            text={createLabel}
            size={buttonSize}
            iconLeft={true}
            icon={ROBOT_2_ICON}
            on:click={startCreation}
          />
          {#if showImport}
          <Button
            kind={darkStartScreen ? "paxAI" : "secondary"}
            text={importLabel}
            size={buttonSize}
            iconLeft={true}
            icon={UPLOAD_ICON}
            on:click={startImport}
          />
          {/if}
        </div>
      </div>
    {:else if processing}
      <div class="processing-state" role="status" aria-live="polite">
        <h1>{processingTitle}</h1>
        <p>{processingMessage}</p>
      </div>
    {:else}
      <div class="greeting">
        <h1>{greeting}</h1>
      </div>

      {#if showSuggestions}
        <div class="suggestions-slot">
          <slot name="suggestions" />
        </div>
      {/if}

      {#if showComposer}
        {#if allowFileUpload && selectedFiles.length}
          <div class="selected-files" aria-label="Selected files">
            {#each selectedFiles as file, index}
              <div class="selected-file">
                <div class="selected-file-details">
                  <span class="selected-file-name" title={file.name}
                    >{file.name}</span
                  >
                  <span class="selected-file-type">{getFileType(file)}</span>
                </div>
                <button
                  class="selected-file-remove"
                  type="button"
                  aria-label={`Remove ${file.name}`}
                  title="Remove file"
                  on:click={() => removeSelectedFile(index)}
                >
                  <img src="/img/assistant/icons/close_dark.svg" alt="" />
                </button>
              </div>
            {/each}
          </div>
        {/if}
      <div class="composer-row">
      {#if showBackButton}
        <button
          class="composer-back"
          type="button"
          aria-label="Back"
          on:click={() => dispatch("back")}
        >
          <img src="/img/assistant/icons/arrow_left.svg" alt="" />
        </button>
      {/if}
      <form class="chat-composer" class:multiline={isMultiline} class:has-upload={allowFileUpload} on:submit|preventDefault={handleSubmit}>
      {#if allowFileUpload}
        <button
          class="voice-button add-button"
          type="button"
          aria-label="Add attachment"
          title="Add files"
          on:click={openFilePicker}
        >
          <img src="/img/ui_icons/add.svg" alt="" />
        </button>
        <input
          bind:this={fileInput}
          class="file-input"
          type="file"
          {accept}
          {multiple}
          on:change={handleFilesSelected}
        />
      {/if}
      <textarea
          bind:this={messageInput}
          bind:value={message}
          class:multiline={isMultiline}
          rows="1"
          aria-label="Message Pax AI"
          placeholder={placeholder}
          on:input={resizeMessageInput}
      ></textarea>
      <div class="composer-actions">
          {#if dictate}
            <button class="composer-icon" type="button" aria-label="Microphone">
              <img src="/img/assistant/icons/mic.svg" alt="" />
            </button>
          {/if}
          {#if graphicEq || message.trim() || selectedFiles.length}
            <button
              class="voice-button"
              class:hasText={message.trim() || selectedFiles.length}
              type="submit"
              disabled={submitDisabled}
              aria-label={submitLabel || (message.trim() ? "Send message" : "Send files")}
              title={submitLabel || (message.trim() ? "Send message" : "Send files")}
            >
              <img
                src={message.trim() || selectedFiles.length
                  ? "/img/assistant/icons/arrow_up.svg"
                  : "/img/assistant/icons/graphic_eq.svg"}
                alt=""
              />
            </button>
          {/if}
      </div>
      </form>
      </div>
      {/if}
      <slot name="afterComposer" />
    {/if}
  </main>
  <slot name="import" />
</div>

<style>
  .paxbot-fullscreen-chat {
    display: flex;
    position: relative;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: var(--pax-white-new);
    color: var(--pax-text-inverse-new);
  }

  .paxbot-fullscreen-chat.started {
    background: var(--pax-bg-canvas-new);
    color: var(--pax-text-primary-new);
  }

  .paxbot-fullscreen-chat.hasInput::after {
    position: fixed;
    right: auto;
    bottom: 0;
    left: var(--paxbot-view-left);
    width: var(--paxbot-view-width);
    height: 6rem;
    z-index: 9;
    background: var(--pax-bg-canvas-new);
    content: "";
  }

  .paxbot-fullscreen-chat.dark-start {
    background: var(--pax-bg-canvas-new);
    color: var(--pax-text-primary-new);
  }

  .paxbot-fullscreen-chat.hasInput {
    align-items: center;
  }

  .chat-progress {
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    width: 100%;
    min-height: 4rem;
    padding: 0.75rem 1.5rem;
    box-sizing: border-box;
    border-bottom: 1px solid var(--pax-border-new);
    background: var(--pax-bg-canvas-new);
    container-type: inline-size;
  }

  .has-progress .chat-stage {
    height: auto;
    min-height: 0;
    overflow-y: auto;
  }

  .has-progress:not(.hasInput) .chat-stage {
    justify-content: safe center;
  }

  .chat-stage {
    display: flex;
    width: min(100%, 48rem);
    height: 100%;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.75rem;
    padding: 1.5rem;
    box-sizing: border-box;
  }

  .hasInput .chat-stage {
    justify-content: flex-start;
    overflow-y: auto;
    padding-bottom: 9rem;
    scrollbar-color: var(--pax-white-new) transparent;
    scrollbar-width: thin;
  }

  .center-content .chat-stage {
    justify-content: center;
    padding-bottom: 1.5rem;
  }

  .hasInput .chat-stage::-webkit-scrollbar {
    width: 0.5rem;
    background: transparent;
  }

  .hasInput .chat-stage::-webkit-scrollbar-track {
    background: transparent;
  }

  .hasInput .chat-stage::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: var(--pax-white-new);
  }

  .hasInput .greeting {
    display: none;
  }

  .hasInput.show-greeting-when-input .greeting {
    display: flex;
  }

  .hasInput.show-greeting-when-input .suggestions-slot {
    margin-top: -1.25rem;
  }

  .hasInput .suggestions-slot {
    margin-top: 0;
  }

  .hasInput .composer-row {
    position: fixed;
    bottom: 1.5rem;
    left: calc(var(--paxbot-view-left) + var(--paxbot-view-width) / 2);
    width: min(calc(var(--paxbot-view-width) - 3rem), 48rem);
    margin-top: 0;
    z-index: 10;
    flex-shrink: 0;
    padding-top: 1rem;
    box-sizing: border-box;
    transform: translateX(-50%);
    background: linear-gradient(
      to bottom,
      transparent,
      var(--pax-bg-canvas-new) 35%
    );
  }

  .suggestions-slot {
    margin-top: -1.25rem;
    margin-bottom: -1.25rem;
  }

  .start-screen {
    display: flex;
    width: 100%;
    max-width: 48rem;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
    text-align: center;
  }

  .start-screen h1 {
    font-size: 1.5rem;
    font-weight: 400;
    line-height: 1.3;
  }

  .start-subtitle {
    margin: -1rem 0 0;
    color: var(--pax-grey-new);
    font-size: 0.8rem;
    line-height: 1.4;
  }

  .dark-start .start-subtitle {
    color: var(--pax-text-secondary-new);
  }

  .start-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.75rem;
  }

  @media (max-width: 42rem) {
    .start-actions {
      flex-direction: column;
      align-items: center;
    }
  }

  h1 {
    margin: 0;
    color: var(--pax-text-inverse-new);
    font-size: 1.5rem;
    font-weight: 400;
    line-height: 1.3;
  }

  .greeting {
    display: flex;
    align-items: center;
    gap: 0.7rem;
  }

  .processing-state {
    display: flex;
    margin: auto;
    flex-direction: column;
    align-items: center;
    gap: 0.65rem;
    text-align: center;
  }

  .processing-state h1 {
    position: relative;
  }

  .processing-state h1::after {
    position: absolute;
    top: 50%;
    left: calc(100% + 0.6rem);
    display: inline-block;
    width: 0.9rem;
    height: 0.9rem;
    border-radius: 50%;
    background: var(--pax-white-new);
    content: "";
    animation: processing-pulse 1.4s ease-in-out infinite;
    transform: translateY(-50%);
  }

  @keyframes processing-pulse {
    0%,
    100% {
      opacity: 0.25;
    }
    50% {
      opacity: 1;
    }
  }

  .processing-state p {
    margin: 0;
    color: var(--pax-text-secondary-new);
    font-size: 0.8rem;
  }

  .started h1 {
    color: var(--pax-white-new);
  }

  .dark-start h1 {
    color: var(--pax-white-new);
  }

  .chat-composer {
    display: flex;
    width: 100%;
    flex: 1;
    min-width: 0;
    min-height: 3.25rem;
    align-items: center;
    gap: 0.6rem;
    padding: 0.45rem 1.25rem;
    box-sizing: border-box;
    border: 1px solid var(--pax-border-strong-new);
    border-radius: 999px;
    background: var(--pax-surface-control-new);
    box-shadow: none;
  }

  .chat-composer.multiline {
    align-items: flex-end;
    border-radius: 1.5rem;
  }

  .chat-composer.has-upload {
    padding-left: 0.5rem;
  }

  .composer-row {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 0.5rem;
  }

  .selected-files {
    display: flex;
    width: 100%;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;
  }

  .selected-file {
    display: flex;
    min-width: 0;
    max-width: 24rem;
    align-items: center;
    gap: 0.65rem;
    padding: 0.55rem 0.65rem;
    border: 1px solid var(--pax-border-strong-new);
    border-radius: 0.75rem;
    background: var(--pax-surface-control-new);
  }

  .selected-file-details {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.15rem;
  }

  .selected-file-name {
    overflow: hidden;
    color: var(--pax-white-new);
    font-size: 0.8rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .selected-file-type {
    color: var(--pax-text-secondary-new);
    font-size: 0.75rem;
    font-weight: 500;
  }

  .selected-file-remove {
    display: grid;
    width: 2rem;
    height: 2rem;
    flex: 0 0 2rem;
    place-items: center;
    box-sizing: border-box;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: var(--pax-white-new);
    color: var(--pax-black-new);
  }

  .selected-file-remove img {
    width: 0.75rem;
    height: 0.75rem;
  }

  .selected-file-remove:hover {
    background: var(--pax-grey-new);
  }

  .composer-back {
    display: grid;
    width: 3.25rem;
    height: 3.25rem;
    flex: 0 0 auto;
    place-items: center;
    border: 1px solid var(--pax-border-strong-new);
    border-radius: 999px;
    background: var(--pax-surface-control-new);
    cursor: pointer;
  }

  .composer-back img {
    width: 1.1rem;
    height: 1.1rem;
    filter: brightness(0) invert(1);
  }

  .composer-back:hover {
    background: var(--pax-white-new);
  }

  .composer-back:hover img {
    filter: brightness(0);
  }

  textarea {
    flex: 1;
    min-width: 0;
    height: 2.35rem;
    min-height: 2.35rem;
    max-height: 7.9rem;
    box-sizing: border-box;
    resize: none;
    border: 0;
    outline: 0;
    margin: 0;
    padding: 0.475rem 0;
    background: transparent;
    color: var(--pax-white-new);
    font: inherit;
    font-size: 0.9rem;
    line-height: 1.4rem;
    overflow-wrap: anywhere;
    overflow-y: hidden;
    scrollbar-color: var(--pax-white-new) transparent;
    scrollbar-width: thin;
  }

  textarea::-webkit-scrollbar {
    width: 0.5rem;
  }

  textarea.multiline {
    overflow-y: auto;
  }

  textarea::-webkit-scrollbar-track {
    background: transparent;
  }

  textarea::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: var(--pax-white-new);
  }

  textarea::placeholder {
    color: var(--pax-grey-new);
  }

  button {
    border: 0;
    cursor: pointer;
  }

  .composer-icon {
    display: grid;
    width: 1.75rem;
    height: 1.75rem;
    flex: 0 0 1.75rem;
    place-items: center;
    padding: 0;
    border-radius: 0.5rem;
    background: transparent;
  }

  .composer-icon:hover {
    background: var(--pax-surface-control-hover-new);
  }

  .composer-icon img {
    width: 1.15rem;
    height: 1.15rem;
  }

  .add-button img {
    filter: brightness(0);
  }

  .add-button:disabled {
    cursor: default;
    opacity: 0.55;
  }

  .file-input {
    display: none;
  }

  .composer-actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .voice-button {
    display: grid;
    width: 2rem;
    height: 2rem;
    place-items: center;
    padding: 0;
    border-radius: 100%;
    background: var(--pax-white-new);
  }

  .voice-button:hover {
    background: var(--pax-grey-new);
  }

  .voice-button img {
    width: 1.1rem;
    height: 1.1rem;
  }

  .voice-button.hasText img {
    filter: brightness(0);
  }

  .voice-button.hasText {
    background: var(--pax-white-new);
  }
</style>
